import type { AdminSession } from '@qaytim/shared';

/**
 * Admin session storage.
 *
 * Deliberately `sessionStorage`, not `localStorage`: an admin token that survives closing the
 * tab is a long-lived credential on a shared office machine. Admin auth is a separate realm from
 * user auth (§4.13) and there is no refresh token — an expired admin session re-logs in.
 */
const STORAGE_KEY = 'qaytim.admin.session';

type Listener = (session: AdminSession | null) => void;

let current: AdminSession | null = null;
const listeners = new Set<Listener>();

function read(): AdminSession | null {
  try {
    const raw = sessionStorage.getItem(STORAGE_KEY);
    return raw ? (JSON.parse(raw) as AdminSession) : null;
  } catch {
    // A corrupt entry must not brick the login page.
    return null;
  }
}

export const authStore = {
  get(): AdminSession | null {
    if (current === null) current = read();
    return current;
  },

  set(session: AdminSession | null): void {
    current = session;
    try {
      if (session) sessionStorage.setItem(STORAGE_KEY, JSON.stringify(session));
      else sessionStorage.removeItem(STORAGE_KEY);
    } catch {
      // Private-mode storage failures are survivable — the session just lives in memory.
    }
    for (const listener of listeners) listener(session);
  },

  clear(): void {
    authStore.set(null);
  },

  subscribe(listener: Listener): () => void {
    listeners.add(listener);
    return () => listeners.delete(listener);
  },

  /** Test seam — resets the module-level cache between tests. */
  reset(): void {
    current = null;
    listeners.clear();
  },
};

export function isAuthenticated(): boolean {
  return Boolean(authStore.get()?.accessToken);
}
